import assert from 'assert';

import {EdgeCondition} from 'analysis-tool/flowchart/edges/EdgeConditionTypes';
import {
  PipelineStepDataKind,
  PipelineStepType,
  PipelineType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import APIUtil from 'util/APIUtil';
import {JSONValue, Override} from 'util/Types';

import {PIPELINE_STEP_SHAPES} from './GeneratedPipelineStepShapes';
import {PipelineFragment} from './graphql/__generated/PipelineFragment';
import {PipelineViewFragment} from './graphql/__generated/PipelineViewFragment';
import {getTypenameForStepDataKind} from './RawStepDataKindRegistry';
import {PipelineConfigJsonSpec, StepConfigShape} from './StepShapeUtils';

export function getPipelineType(pipelineId: string): PipelineType {
  if (APIUtil.isAnalysis(pipelineId)) {
    return PipelineType.ANALYSIS;
  } else if (APIUtil.isAnalysisTemplate(pipelineId)) {
    return PipelineType.ANALYSIS_TEMPLATE;
  } else {
    // Other pipeline types are not supported by the UI
    assert(false, 'Unexpected pipeline type');
  }
}

export type PipelineGraphQL = Override<PipelineFragment, {steps: Array<PipelineStep>}>;

type _RawStep = PipelineFragment['steps'][number];

export type PipelineStep<
  StepType extends PipelineStepType = PipelineStepType,
  StepVersion extends number = number,
> = Override<
  _RawStep,
  {
    config: PipelineStepConfig<StepType, StepVersion> | null;
    stepType: StepType;
    stepVersion: StepVersion;
    incomingEdges: Array<Override<_RawStep['incomingEdges'][number], {condition: EdgeCondition | null}>>;
    outgoingEdges: Array<Override<_RawStep['outgoingEdges'][number], {condition: EdgeCondition | null}>>;
  }
>;

export type PipelineViewGraphQL = PipelineViewFragment;
export type PipelineViewLineageNode = NonNullable<PipelineViewGraphQL['lineage'][number]>;
export type PipelineStepOutputReference = NonNullable<PipelineViewLineageNode['stepOutputReference']>;

export type PipelineStepData = PipelineStep['inputStepDatas'][number];

export type PipelineEdge = PipelineStep['incomingEdges'][number];

type _RawData = PipelineStepData['rawDatas'][number];

export type PipelineStepRawData<Kind extends PipelineStepDataKind | null = null> =
  Kind extends PipelineStepDataKind.CHART
    ? Extract<_RawData, {__typename: 'ChartNode'}>
    : Kind extends PipelineStepDataKind.DATASET
      ? Extract<_RawData, {__typename: 'DataFrameDomainNode'}>
      : Kind extends PipelineStepDataKind.FILE
        ? Extract<_RawData, {__typename: 'ScratchFileNode'}>
        : Kind extends PipelineStepDataKind.PLATE
          ? Extract<_RawData, {__typename: 'ScratchPlateNode'}>
          : _RawData;

/**
 * Narrow `PipelineStep` to a specific step type, to access typed config and step datas.
 *
 * Example:
 *
 *    if (pipelineStepIsStepType(PipelineStepType.SORT_COLUMNS, 0, step)) {
 *        const {columns} = getRawDatasByStepDataName(step, 'Input dataset')[0];
 *        const {sortOptions} = step.config;
 *    }
 */
export function pipelineStepIsStepType<StepType extends PipelineStepType, StepVersion extends number>(
  stepType: StepType,
  stepVersion: StepVersion,
  step: PipelineStep
): step is PipelineStep<StepType, StepVersion> {
  return step.stepType === stepType && step.stepVersion === stepVersion;
}

/**
 * Assert `PipelineStep` is specific step type, to access typed config and step datas.
 *
 * Example:
 *
 *    assertPipelineStepIsStepType(PipelineStepType.SORT_COLUMNS, 0, step);
 *    const {sortOptions} = step.config;
 */
export function assertPipelineStepIsStepType<StepType extends PipelineStepType, StepVersion extends number>(
  stepType: StepType,
  stepVersion: StepVersion,
  step: PipelineStep
): asserts step is PipelineStep<StepType, StepVersion> {
  assert(
    pipelineStepIsStepType(stepType, stepVersion, step),
    `Expected ${stepType} v${stepVersion} step but found ${step.stepType} v${step.stepVersion}`
  );
}

/**
 * Assert that a pipeline step's config is non-null, narrowing the step type.
 *
 * @example
 *
 *    assertPipelineStepIsStepType(PipelineStepType.SORT_COLUMNS, 0, step);
 *    assertPipelineStepConfigNonNull(step);
 *    const {sortOptions} = step.config;
 */
export function assertPipelineStepConfigNonNull<
  StepType extends PipelineStepType,
  StepVersion extends number,
>(
  step: PipelineStep<StepType, StepVersion>
): asserts step is PipelineStep<StepType, StepVersion> & {
  config: NonNullable<PipelineStep<StepType, StepVersion>['config']>;
} {
  assert(step.config != null, `Step config is unexpectedly null for ${step.stepType} v${step.stepVersion}`);
}

/**
 * Narrow `PipelineStepRawData` to a specific kind.
 */
export function pipelineStepRawDataIsKind<TPipelineStepDataKind extends PipelineStepDataKind>(
  rawData: PipelineStepRawData,
  kind: TPipelineStepDataKind
): rawData is PipelineStepRawData<TPipelineStepDataKind> {
  return getTypenameForStepDataKind(kind) === rawData.__typename;
}

/**
 * Assert `PipelineStepRawData` is a specific kind.
 */
export function assertPipelineStepRawDataIsKind<TPipelineStepDataKind extends PipelineStepDataKind>(
  rawData: PipelineStepRawData,
  kind: TPipelineStepDataKind
): asserts rawData is PipelineStepRawData<TPipelineStepDataKind> {
  const expectedTypename = getTypenameForStepDataKind(kind);
  assert(
    expectedTypename != null,
    `No __typename is registered for raw step data kind ${kind}; register it in RawStepDataKindRegistry`
  );
  assert(
    rawData.__typename === expectedTypename,
    `Expected __typename to be ${expectedTypename} for kind ${kind} but found ${rawData.__typename}`
  );
}

/**
 * Utility for getting the step shape associated with the input StepType and StepVersion
 *
 * @example
 * type InitializeDatasetV1Shape = PipelineStepShape<PipelineStepType.INITIALIZE_DATASET, 1>
 *  -> {
 *   stepType: PipelineStepType.INITIALIZE_DATASET;
 *   version: 1;
 *   inputDataShapes: {};
 *   outputDataShapes: {
 *     Dataset: {
 *       isMulti: false;
 *       isRequired: true;
 *     };
 *   };
 *   config: {...};
 * }
 */
export type GetPipelineStepShape<
  StepType extends PipelineStepType,
  StepVersion extends number,
> = (typeof PIPELINE_STEP_SHAPES)[StepType][StepVersion] extends {}
  ? (typeof PIPELINE_STEP_SHAPES)[StepType][StepVersion]
  : never;

type _PipelineJsonSpecToTsType<
  JsonSpec extends PipelineConfigJsonSpec,
  Status extends 'draft' | 'complete',
> = JsonSpec extends {
  kind: 'string';
  // Extract the value as a `StringLiteral` variable if it is present
  value: infer StringLiteral extends string;
}
  ? // e.g. {kind: 'string', value: 'foo'}
    // -> 'foo'
    StringLiteral
  : JsonSpec extends {kind: 'string'}
    ? string
    : JsonSpec extends {kind: 'columnReference'}
      ? string
      : JsonSpec extends {kind: 'domain'}
        ? string
        : JsonSpec extends {kind: 'number' | 'integer'}
          ? number
          : JsonSpec extends {kind: 'boolean'}
            ? boolean
            : JsonSpec extends {kind: 'enum'}
              ? // e.g. {kind: 'enum', choices: ['foo', 'bar']}
                // -> 'foo' | 'bar'
                JsonSpec['choices'][number]
              : JsonSpec extends {
                    kind: 'array';
                    elementSpec: infer ElementSpec extends PipelineConfigJsonSpec;
                  }
                ? // e.g. {kind: 'array', elementSpec: {kind: 'string', required: false}}
                  // -> Array<string | null>
                  Array<PipelineJsonSpecToTsType<ElementSpec, Status>>
                : JsonSpec extends {
                      kind: 'shape';
                      fields: infer Fields extends Record<string, PipelineConfigJsonSpec>;
                    }
                  ? // e.g. {kind: 'shape', fields: {foo: {kind: 'string', required: false}, bar: {kind: 'number'}}}
                    // -> {foo: string | null, bar: number}
                    {
                      [FieldName in keyof Fields]: PipelineJsonSpecToTsType<Fields[FieldName], Status>;
                    }
                  : JsonSpec extends {
                        kind: 'taggedUnion';
                        choices: infer ChoiceArray extends ReadonlyArray<
                          Extract<PipelineConfigJsonSpec, {kind: 'shape'}>
                        >;
                      }
                    ? // e.g. {
                      //   kind: 'taggedUnion',
                      //   tagKey: 'tag',
                      //   choices: [
                      //     {kind: 'object', fields: {tag: {kind: 'string', value: 'A'}}},
                      //     {kind: 'object', fields: {tag: {kind: 'string', value: 'B'}, foo: {kind: 'string', required: false}},
                      //   ]
                      // }
                      // -> {tag: 'A'} | {tag: 'B', foo: string | null}
                      PipelineJsonSpecToTsType<ChoiceArray[number], Status>
                    : JsonSpec extends {kind: 'anything'}
                      ? JSONValue
                      : JsonSpec extends {kind: 'color'}
                        ? string
                        : JsonSpec extends {kind: 'columnValue'}
                          ? JSONValue
                          : never;

/**
 * Utility for getting a string literal union of the possible tag values of a tagged union spec, or `never`
 * if the input is not a tagged union spec.
 *
 * @example
 * GetTaggedUnionTagValues<{
 *    kind: 'taggedUnion',
 *    tagKey: 'tag',
 *    choices: [
 *      {kind: 'object', fields: {tag: {kind: 'string', value: 'A'}}},
 *      {kind: 'object', fields: {tag: {kind: 'string', value: 'B'}, foo: {kind: 'string', required: false}},
 *    ]
 *  }>
 *  -> 'A' | 'B'
 */
export type GetTaggedUnionTagValues<JsonSpec extends PipelineConfigJsonSpec> = JsonSpec extends {
  kind: 'taggedUnion';
  tagKey: infer TagKey extends string;
  choices: ReadonlyArray<infer Choice extends PipelineConfigJsonSpec>;
}
  ? Choice extends {
      kind: 'shape';
      fields: {[key in TagKey]: {kind: 'string'; value: infer TagValue extends string}};
    }
    ? TagValue
    : never
  : never;

/**
 * Utility for converting a Pipeline JSON Spec to the typescript type of values that match that spec.
 *
 * If `Status` is `'draft'`, then fields are allowed to be nullable with a few exceptions:
 * - Shapes
 * - Arrays
 * - Strings with defined `value`s
 * This behavior should be kept in sync with the values returned by `jsonSpecToInitialValue` and the types
 * of values defined by `StepConfigEditorModal`/`StepConfigEditorRouter`.
 *
 * @example
 * PipelineJsonSpecToTsType<{kind: 'array', elementSpec: {kind: 'string', required: false}}>;
 * -> Array<string | null>
 *
 * @see examples in PipelineTypes-test.ts
 *
 * @package exported for testing
 */
export type PipelineJsonSpecToTsType<
  JsonSpec extends PipelineConfigJsonSpec,
  Status extends 'draft' | 'complete' = 'complete',
> =
  // non-required fields are always nullable
  JsonSpec extends {
    required: false;
  }
    ? _PipelineJsonSpecToTsType<JsonSpec, Status> | null
    : Status extends 'draft'
      ? JsonSpec extends
          | {
              // draft arrays and shapes are always initialized to objects/arrays
              kind: 'array' | 'shape';
            }
          | {
              // draft strings with values (e.g. tag keys) are always initialized to a string
              kind: 'string';
              value: string;
            }
        ? _PipelineJsonSpecToTsType<JsonSpec, Status>
        : // drafts of everything else can be initialized to null
          _PipelineJsonSpecToTsType<JsonSpec, Status> | null
      : _PipelineJsonSpecToTsType<JsonSpec, Status>;

/**
 * Utility for getting the type of the config associated with the input StepType and StepVersion
 *
 * @example PipelineStepConfig<PipelineStepType.INITIALIZE_DATASET, 0> -> {} (no config)
 * @example
 * ```
 * PipelineStepConfig<PipelineStepType.SORT_COLUMNS, 0> ->
 * Array<{
 *   sortOptions: {
 *     column: string;
 *     direction: "ASCENDING" | "DESCENDING";
 *   }
 * }>
 * ```
 */
export type PipelineStepConfig<
  StepType extends PipelineStepType,
  StepVersion extends number,
  /** Persisted step configs are never in `draft` state. */
  Status extends 'draft' | 'complete' = 'complete',
> = GetPipelineStepShape<StepType, StepVersion>['config'] extends infer Config extends StepConfigShape
  ? {
      [VariableName in keyof Config]: PipelineJsonSpecToTsType<Config[VariableName], Status>;
    }
  : {};
